import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Dashboard.css';
import amplifyconfig from './amplifyconfiguration.json';
import { COUNTRIES } from './data/countries';

// Format a Date as YYYY-MM-DD for the dashboard API
const formatDateForApi = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Short human label for a range, e.g. "Apr 1 – Apr 30, 2026"
const formatRangeLabel = (start, end) => {
  if (!start || !end) return '';
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
};

// Dashboard password from environment variable
const DASHBOARD_PASSWORD = process.env.REACT_APP_DASHBOARD_PASSWORD || 'admin123';

// Common timezones with their UTC offsets in minutes
const TIMEZONES = [
  { label: 'UTC', offset: 0 },
  { label: 'EST (New York)', offset: -300 },
  { label: 'CST (Chicago)', offset: -360 },
  { label: 'MST (Denver)', offset: -420 },
  { label: 'PST (Los Angeles)', offset: -480 },
  { label: 'CET (Berlin/Paris)', offset: 60 },
  { label: 'EET (Athens)', offset: 120 },
  { label: 'GST (Dubai)', offset: 240 },
  { label: 'IST (Mumbai)', offset: 330 },
  { label: 'CST (Shanghai)', offset: 480 },
  { label: 'JST (Tokyo)', offset: 540 },
  { label: 'AEST (Sydney)', offset: 600 },
];

// Country filter options
const COUNTRY_FILTERS = [
  { value: 'all', label: 'All Countries' },
  { value: 'dach', label: 'DACH (DE, AT, CH)' },
  { value: 'non-dach', label: 'Non-DACH' },
];

// DACH country codes
const DACH_COUNTRIES = ['DE', 'AT', 'CH'];

/**
 * Dashboard Component
 *
 * Password-protected dashboard showing registration statistics:
 * - Total Registrations
 * - Registrations Today
 * - Confirmed Percentage
 */
function Dashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timezone, setTimezone] = useState(() => {
    // Load saved timezone or default to browser's timezone
    const saved = localStorage.getItem('dashboard_timezone');
    if (saved) return parseInt(saved, 10);
    // Get browser's timezone offset (note: getTimezoneOffset returns opposite sign)
    return -new Date().getTimezoneOffset();
  });
  const [countryFilter, setCountryFilter] = useState(() => {
    // Load saved country filter or default to 'all'
    return localStorage.getItem('dashboard_country_filter') || 'all';
  });
  const [specificCountry, setSpecificCountry] = useState(() => {
    // Load saved specific country or default to empty (use region filter)
    return localStorage.getItem('dashboard_specific_country') || '';
  });
  const [availableCountries, setAvailableCountries] = useState([]);
  // Date range scopes ONLY the totals cards (Unique Registrations + Confirmed).
  // Today / Last 7 Days always reflect the current date.
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  // Check if already authenticated on mount
  useEffect(() => {
    const auth = localStorage.getItem('dashboard_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch stats when authenticated, timezone, country filter, or date range changes.
  // Only refetch on range change once both ends are set (or both cleared).
  useEffect(() => {
    if (!isAuthenticated) return;
    if ((rangeStart && !rangeEnd) || (!rangeStart && rangeEnd)) return;
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, timezone, countryFilter, specificCountry, rangeStart, rangeEnd]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === DASHBOARD_PASSWORD) {
      localStorage.setItem('dashboard_auth', 'true');
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('dashboard_auth');
    setIsAuthenticated(false);
    setStats(null);
  };

  const handleTimezoneChange = (e) => {
    const newTimezone = parseInt(e.target.value, 10);
    setTimezone(newTimezone);
    localStorage.setItem('dashboard_timezone', newTimezone.toString());
  };

  const handleCountryFilterChange = (e) => {
    const newFilter = e.target.value;
    setCountryFilter(newFilter);
    localStorage.setItem('dashboard_country_filter', newFilter);

    // Clear specific country if it's no longer valid for the new region
    if (specificCountry) {
      const isValidForRegion =
        newFilter === 'all' ||
        (newFilter === 'dach' && DACH_COUNTRIES.includes(specificCountry)) ||
        (newFilter === 'non-dach' && !DACH_COUNTRIES.includes(specificCountry));

      if (!isValidForRegion) {
        setSpecificCountry('');
        localStorage.setItem('dashboard_specific_country', '');
      }
    }
  };

  const handleSpecificCountryChange = (e) => {
    const newCountry = e.target.value;
    setSpecificCountry(newCountry);
    localStorage.setItem('dashboard_specific_country', newCountry);
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get Dashboard API URL from configuration or env var
      const apiUrl = amplifyconfig.custom?.API?.DashboardAPI?.endpoint
        || process.env.REACT_APP_DASHBOARD_API_URL;

      console.log('Dashboard API URL:', apiUrl);
      console.log('Amplify config:', amplifyconfig);

      if (!apiUrl) {
        throw new Error('Dashboard API URL not configured');
      }

      // Add timezone and country filter parameters to API call
      let urlWithParams = `${apiUrl}?tz=${timezone}&country=${countryFilter}`;
      if (specificCountry) {
        urlWithParams += `&specificCountry=${specificCountry}`;
      }
      // Date range scopes only the totals cards (backend ignores it for today/7d)
      if (rangeStart && rangeEnd) {
        urlWithParams += `&startDate=${formatDateForApi(rangeStart)}&endDate=${formatDateForApi(rangeEnd)}`;
      }

      const response = await fetch(urlWithParams, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStats(data);

      // Update available countries from API response
      if (data.availableCountries) {
        setAvailableCountries(data.availableCountries);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  // Password prompt screen
  if (!isAuthenticated) {
    return (
      <div className="dashboard-login">
        <div className="dashboard-login-card">
          <p>Enter password to continue</p>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <div className="password-error">{passwordError}</div>}
            <button type="submit">Access Dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  // Get current timezone label for display
  const currentTimezoneLabel = TIMEZONES.find(tz => tz.offset === timezone)?.label || 'Custom';

  // Main dashboard
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1></h1>
        <div className="header-controls">
          <div className="filter-selector">
            <label htmlFor="countryFilter">Region:</label>
            <select
              id="countryFilter"
              value={countryFilter}
              onChange={handleCountryFilterChange}
              disabled={!!specificCountry}
            >
              {COUNTRY_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-selector">
            <label htmlFor="specificCountry">Country:</label>
            <select
              id="specificCountry"
              value={specificCountry}
              onChange={handleSpecificCountryChange}
            >
              <option value="">All (use region)</option>
              {availableCountries
                .filter((code) => {
                  // Filter countries based on region selection
                  if (countryFilter === 'dach') {
                    return DACH_COUNTRIES.includes(code);
                  } else if (countryFilter === 'non-dach') {
                    return !DACH_COUNTRIES.includes(code);
                  }
                  return true; // 'all' - show all countries
                })
                .map((code) => {
                  const country = COUNTRIES.find(c => c.code === code);
                  return (
                    <option key={code} value={code}>
                      {country?.name || code}
                    </option>
                  );
                })}
            </select>
          </div>
          <div className="filter-selector">
            <label htmlFor="timezone">Timezone:</label>
            <select
              id="timezone"
              value={timezone}
              onChange={handleTimezoneChange}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.offset} value={tz.offset}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-selector date-range-filter">
            <label>Totals range:</label>
            <DatePicker
              selected={rangeStart}
              onChange={(date) => setRangeStart(date)}
              selectsStart
              startDate={rangeStart}
              endDate={rangeEnd}
              maxDate={rangeEnd || new Date()}
              dateFormat="MMM d, yyyy"
              placeholderText="Start"
              className="date-input"
              isClearable
            />
            <span className="date-range-sep">–</span>
            <DatePicker
              selected={rangeEnd}
              onChange={(date) => setRangeEnd(date)}
              selectsEnd
              startDate={rangeStart}
              endDate={rangeEnd}
              minDate={rangeStart}
              maxDate={new Date()}
              dateFormat="MMM d, yyyy"
              placeholderText="End"
              className="date-input"
              isClearable
            />
            {(rangeStart || rangeEnd) && (
              <button
                type="button"
                className="date-range-clear"
                onClick={() => { setRangeStart(null); setRangeEnd(null); }}
              >
                Clear
              </button>
            )}
          </div>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        {loading && (
          <div className="dashboard-loading">
            <div className="spinner"></div>
            <p>Loading statistics...</p>
          </div>
        )}

        {error && (
          <div className="dashboard-error">
            <p>{error}</p>
            <button onClick={fetchStats}>Retry</button>
          </div>
        )}

        {stats && !loading && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-label">Unique Registrations</span>
                <span className="stat-value">{(stats.uniqueRegistrations ?? stats.totalRegistrations).toLocaleString()}</span>
                <span className="stat-subtext">{stats.totalRegistrations.toLocaleString()} total</span>
                {stats.appliedRange && (
                  <span className="stat-range">{formatRangeLabel(rangeStart, rangeEnd)}</span>
                )}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon today">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-label">Registrations Today</span>
                <span className="stat-value">{(stats.uniqueRegistrationsToday ?? stats.registrationsToday).toLocaleString()}</span>
                <span className="stat-subtext">{stats.registrationsToday.toLocaleString()} total</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon week">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="M18 17V9" />
                  <path d="M13 17V5" />
                  <path d="M8 17v-3" />
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-label">Last 7 Days</span>
                <span className="stat-value">{(stats.uniqueRegistrationsLast7Days ?? stats.registrationsLast7Days)?.toLocaleString() || 0}</span>
                <span className="stat-subtext">{stats.registrationsLast7Days?.toLocaleString() || 0} total</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon confirmed">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div className="stat-content">
                <span className="stat-label">Confirmed</span>
                <span className="stat-value">{stats.confirmedPercentage}%</span>
                <span className="stat-subtext">{stats.confirmedCount.toLocaleString()} of {stats.totalRegistrations.toLocaleString()}</span>
                {stats.appliedRange && (
                  <span className="stat-range">{formatRangeLabel(rangeStart, rangeEnd)}</span>
                )}
              </div>
            </div>

            {stats.pageViews && (
              <>
                <div className="stat-card">
                  <div className="stat-icon views">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <span className="stat-label">Unique Page Views</span>
                    <span className="stat-value">{(stats.pageViews.uniqueVisitors ?? stats.pageViews.last7DaysUnique).toLocaleString()}</span>
                    <span className="stat-subtext">{(stats.pageViews.totalViews ?? stats.pageViews.last7DaysTotal).toLocaleString()} total</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon views-today">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <span className="stat-label">Page Views Today</span>
                    <span className="stat-value">{stats.pageViews.todayUnique.toLocaleString()}</span>
                    <span className="stat-subtext">{stats.pageViews.todayTotal.toLocaleString()} total</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon views-week">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <span className="stat-label">Page Views (7 Days)</span>
                    <span className="stat-value">{stats.pageViews.last7DaysUnique.toLocaleString()}</span>
                    <span className="stat-subtext">{stats.pageViews.last7DaysTotal.toLocaleString()} total</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Channel Attribution Section */}
        {stats && !loading && (stats.registrationChannelBreakdown || stats.pageViews?.channelBreakdown) && (
          <div className="channel-attribution">
            <h2>Marketing Channel Attribution</h2>

            <div className="attribution-grid">
              {/* Registration Channel Breakdown */}
              {stats.registrationChannelBreakdown && Object.keys(stats.registrationChannelBreakdown).length > 0 && (
                <div className="attribution-card">
                  <h3>Registrations by Channel</h3>
                  <div className="channel-bars">
                    {Object.entries(stats.registrationChannelBreakdown)
                      .sort((a, b) => b[1].percentage - a[1].percentage)
                      .map(([channel, data]) => (
                        <div key={channel} className="channel-bar">
                          <div className="channel-label">
                            <span>{channel}</span>
                            <span>{data.percentage}% ({data.count.toLocaleString()})</span>
                          </div>
                          <div className="bar-container">
                            <div
                              className="bar-fill"
                              style={{ width: `${data.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Page View Channel Breakdown */}
              {stats.pageViews?.channelBreakdown && Object.keys(stats.pageViews.channelBreakdown).length > 0 && (
                <div className="attribution-card">
                  <h3>Page Views by Channel (7 Days)</h3>
                  <div className="channel-bars">
                    {Object.entries(stats.pageViews.channelBreakdown)
                      .sort((a, b) => b[1].percentage - a[1].percentage)
                      .map(([channel, data]) => (
                        <div key={channel} className="channel-bar">
                          <div className="channel-label">
                            <span>{channel}</span>
                            <span>{data.percentage}% ({data.views.toLocaleString()})</span>
                          </div>
                          <div className="bar-container">
                            <div
                              className="bar-fill views"
                              style={{ width: `${data.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {stats && (
          <div className="dashboard-footer">
            <p>Last updated: {new Date(stats.timestamp).toLocaleString()}</p>
            <button onClick={fetchStats} className="refresh-button">
              Refresh
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
